import React from 'react';

/**
 * Reusable pagination bar. Accepts either totalPages or totalItems+pageSize.
 * Props:
 *  - page (number, 1-based)
 *  - pageSize (number, default 20)
 *  - totalItems (number) — if provided, used to compute totalPages
 *  - totalPages (number) — optional, overrides computed value
 *  - onChange (fn) — called with new page number
 *  - className (string)
 */
export default function PaginationBar({
    page,
    pageSize = 20,
    totalItems,
    totalPages: totalPagesProp,
    onChange,
    className = '',
}) {
    const totalPages = Math.max(
        1,
        totalPagesProp ?? Math.ceil((totalItems || 0) / pageSize)
    );

    const setPage = (p) => {
        const next = typeof p === 'function' ? p(page) : p;
        const clamped = Math.min(Math.max(1, next), totalPages);
        onChange?.(clamped);
    };

    return (
        <div className={`flex items-center gap-3 justify-between p-3 bg-white border rounded-xl ${className}`}>
            <div className="text-sm text-gray-600">
                Page <strong>{page}</strong> of <strong>{totalPages}</strong>
                {typeof totalItems === 'number' && (
                    <> · <span className="text-gray-500">{totalItems} total</span></>
                )}
            </div>

            <div className="flex items-center gap-2">
                <button
                    onClick={() => setPage(1)}
                    disabled={page <= 1}
                    className="px-3 py-1 border rounded-lg bg-white hover:bg-gray-100 disabled:opacity-40"
                >
                    « First
                </button>
                <button
                    onClick={() => setPage((p) => p - 1)}
                    disabled={page <= 1}
                    className="px-3 py-1 border rounded-lg bg-white hover:bg-gray-100 disabled:opacity-40"
                >
                    Prev
                </button>
                <div className="px-3 py-1 border rounded-lg bg-white text-sm">{page}</div>
                <button
                    onClick={() => setPage((p) => p + 1)}
                    disabled={page >= totalPages}
                    className="px-3 py-1 border rounded-lg bg-white hover:bg-gray-100 disabled:opacity-40"
                >
                    Next
                </button>
                <button
                    onClick={() => setPage(totalPages)}
                    disabled={page >= totalPages}
                    className="px-3 py-1 border rounded-lg bg-white hover:bg-gray-100 disabled:opacity-40"
                >
                    Last »
                </button>
            </div>
        </div>
    );
}
